import React from 'react';
import PidDiagramShell, { PidPipe, PidPort } from './PidDiagramShell';
import { OlefinRoute } from '../../types';

/** 加氢裂化装置 P&ID 详细图：两段反应 + 高低压分离 + 循环氢 + 分馏 */
const media = {
  oil: { label: '减压蜡油 / 生成油', light: '#ef6c00', dark: '#ffb74d', width: 2.6 },
  h2: { label: '氢气 / 循环氢', light: '#2e7d32', dark: '#81c784', width: 2.6 },
  steam: { label: '蒸汽', light: '#d32f2f', dark: '#e57373', width: 2 },
  water: { label: '水', light: '#1565c0', dark: '#64b5f6', width: 2 },
  fuel: { label: '燃料气', light: '#c2185b', dark: '#f06292', width: 2, dash: '7 4' },
};

const devices = [
  { id: 'v101', kind: 'hDrum' as const, x: 115, y: 182, tag: 'V-101', name: '原料缓冲罐' },
  { id: 'p101', kind: 'pump' as const, x: 270, y: 193, tag: 'P-101', name: '进料泵' },
  { id: 'e101', kind: 'exchanger' as const, x: 370, y: 181, tag: 'E-101', name: '原料/生成油换热' },
  { id: 'mix101', kind: 'mixer' as const, x: 500, y: 225, tag: '', name: '' },
  { id: 'f101', kind: 'furnace' as const, x: 560, y: 146, tag: 'F-101', name: '加热炉', note: '出口 400℃' },
  { id: 'r101', kind: 'reactor' as const, x: 760, y: 128, tag: 'R-101', name: '加氢精制', note: '370℃' },
  { id: 'r102', kind: 'reactor' as const, x: 930, y: 128, tag: 'R-102', name: '加氢裂化', note: '390℃' },
  { id: 'e102', kind: 'exchanger' as const, x: 1090, y: 181, tag: 'E-102', name: '生成油换热' },
  { id: 'v102', kind: 'vDrum' as const, x: 1220, y: 154, tag: 'V-102', name: '高压分离器', note: '15.7MPa' },
  { id: 'v103', kind: 'vDrum' as const, x: 1360, y: 161, tag: 'V-103', name: '低压分离器' },
  { id: 'c101', kind: 'compressor' as const, x: 300, y: 430, tag: 'C-101', name: '循环氢压缩机' },
  { id: 't201', kind: 'column' as const, x: 1520, y: 101, tag: 'T-201', name: '分馏塔' },
];

const ports: PidPort[] = [
  { id: 'p-feed', label: '减压蜡油', medium: 'oil', x: 0, y: 224, direction: 'in' as const },
  { id: 'p-h2new', label: '新氢', medium: 'h2', x: 180, y: 445, direction: 'in' as const },
  { id: 'p-fg', label: '燃料气', medium: 'fuel', x: 520, y: 648, direction: 'in' as const },
  { id: 'p-wash', label: '洗涤水', medium: 'water', x: 1100, y: 120, direction: 'in' as const },
  { id: 'p-naphtha', label: '石脑油', medium: 'oil', x: 1740, y: 52, direction: 'out' as const },
  { id: 'p-kero', label: '航空煤油', medium: 'oil', x: 1720, y: 224, direction: 'out' as const },
  { id: 'p-diesel', label: '柴油', medium: 'oil', x: 1720, y: 611, direction: 'out' as const },
];

const pipes: PidPipe[] = [
  { id: 's01', from: 'p-feed', fromSide: 'right', to: 'v101', toSide: 'left', medium: 'oil' },
  { id: 's02', from: 'v101', fromSide: 'right', to: 'p101', toSide: 'left', medium: 'oil' },
  { id: 's03', from: 'p101', fromSide: 'right', to: 'e101', toSide: 'left', medium: 'oil' },
  { id: 's04', from: 'e101', fromSide: 'right', to: 'mix101', toSide: 'left', medium: 'oil' },
  { id: 's05', from: 'mix101', fromSide: 'right', to: 'f101', toSide: 'left', medium: 'oil' },
  { id: 's06', from: 'f101', fromSide: 'right', to: 'r101', toSide: 'left', medium: 'oil' },
  { id: 's07', from: 'r101', fromSide: 'right', to: 'r102', toSide: 'left', medium: 'oil' },
  { id: 's08', from: 'r102', fromSide: 'right', to: 'e102', toSide: 'left', medium: 'oil' },
  { id: 's09', from: 'e102', fromSide: 'right', to: 'v102', toSide: 'left', medium: 'oil' },
  { id: 's10', from: 'v102', fromSide: 'right', to: 'v103', toSide: 'left', medium: 'oil' },
  { id: 's11', from: 'v103', fromSide: 'right', to: 't201', toSide: 'left', medium: 'oil' },
  // 循环氢：高分顶 → 压缩机 → 混氢点（顶部走廊 y=115）
  { id: 's12', from: 'v102', fromSide: 'top', to: 'c101', toSide: 'top', medium: 'h2' },
  { id: 's13', from: 'c101', fromSide: 'right', to: 'mix101', toSide: 'bottom', medium: 'h2' },
  { id: 's14', from: 'p-h2new', fromSide: 'right', to: 'c101', toSide: 'left', medium: 'h2' },
  // 分馏三产品
  { id: 's15', from: 't201', fromSide: 'top', to: 'p-naphtha', toSide: 'left', medium: 'oil' },
  { id: 's16', from: 't201', fromSide: 'right', to: 'p-kero', toSide: 'left', medium: 'oil' },
  { id: 's17', from: 't201', fromSide: 'bottom', to: 'p-diesel', toSide: 'left', medium: 'oil' },
  { id: 's18', from: 'p-wash', fromSide: 'right', to: 'v102', toSide: 'top', medium: 'water' },
  { id: 's19', from: 'p-fg', fromSide: 'right', to: 'f101', toSide: 'bottom', medium: 'fuel' },
];

const HydrocrackingDiagram: React.FC<{ route: OlefinRoute }> = ({ route }) => (
  <PidDiagramShell
    routeName={route.name}
    title="加氢裂化装置详细流程"
    subtitle="原料泵 → 换热 → 混氢（新氢+循环氢）→ 加热炉 → 加氢精制/加氢裂化两段反应 → 高低压分离 → 循环氢压缩机返回 → 分馏塔出石脑油/航空煤油/柴油（设备形状按 P&ID 惯例，管线按介质分色）"
    media={media}
    devices={devices}
    ports={ports}
    pipes={pipes}
    feedstock={route.feedstock}
    products={[...route.products, ...(route.byproducts ?? [])]}
  />
);

export default HydrocrackingDiagram;
